import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from "react";

export type ANNAdminSortColumn = { columnKey: string; direction: "ASC" | "DESC" };

export interface ANNAdminTableColumn<T> {
   key: string;
   name: string;
   width?: number;
   minWidth?: number;
   frozen?: boolean;
   sortable?: boolean;
   renderCell: (props: { row: T }) => ReactNode;
}

interface ANNAdminVirtualTableProps<T> {
   columns: ANNAdminTableColumn<T>[];
   rows: T[];
   rowHeight?: number;
   rowKeyGetter: (row: T) => string;
   rowClass?: (row: T) => string;
   sortColumns?: readonly ANNAdminSortColumn[];
   onSortColumnsChange?: (sortColumns: ANNAdminSortColumn[]) => void;
}

const HEADER_HEIGHT = 36;
const DEFAULT_ROW_HEIGHT = 36;
const OVERSCAN = 12;

export function ANNAdminVirtualTable<T>(props: ANNAdminVirtualTableProps<T>) {
   const {
      columns,
      rows,
      rowHeight = DEFAULT_ROW_HEIGHT,
      rowKeyGetter,
      rowClass,
      sortColumns = [],
      onSortColumnsChange,
   } = props;
   const bodyRef = useRef<HTMLDivElement>(null);
   const headerRef = useRef<HTMLDivElement>(null);
   const rafRef = useRef<number>(0);
   const [scrollTop, setScrollTop] = useState(0);
   const [viewportHeight, setViewportHeight] = useState(400);

   useEffect(() => {
      const el = bodyRef.current;
      if (!el) return;
      const update = () => setViewportHeight(el.clientHeight || 400);
      update();
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
   }, []);

   const gridTemplateColumns = useMemo(() => {
      return columns
         .map((column) => {
            if (column.width) {
               return `${column.width}px`;
            }
            const minWidth = column.minWidth || 120;
            return `minmax(${minWidth}px, 1fr)`;
         })
         .join(" ");
   }, [columns]);

   const minTableWidth = useMemo(() => {
      return columns.reduce((sum, column) => sum + (column.width || column.minWidth || 120), 0);
   }, [columns]);

   const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
   const visibleCount = Math.ceil(viewportHeight / rowHeight) + OVERSCAN * 2;
   const endIndex = Math.min(rows.length, startIndex + visibleCount);
   const visibleRows = rows.slice(startIndex, endIndex);
   const totalHeight = rows.length * rowHeight;

   const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
      const nextTop = event.currentTarget.scrollTop;
      const nextLeft = event.currentTarget.scrollLeft;
      if (headerRef.current) {
         headerRef.current.scrollLeft = nextLeft;
      }
      if (rafRef.current) {
         cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = requestAnimationFrame(() => {
         rafRef.current = 0;
         setScrollTop(nextTop);
      });
   }, []);

   useEffect(() => {
      return () => {
         if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
         }
      };
   }, []);

   const handleSort = useCallback((columnKey: string, sortable?: boolean) => {
      if (sortable === false || !onSortColumnsChange) {
         return;
      }
      const current = sortColumns.find((sort) => sort.columnKey === columnKey);
      const direction: "ASC" | "DESC" = current?.direction === "ASC" ? "DESC" : "ASC";
      onSortColumnsChange([{ columnKey, direction }]);
   }, [onSortColumnsChange, sortColumns]);

   return (
      <div className="ann-admin-table">
         <div
            ref={headerRef}
            className="ann-admin-table__header"
            style={{ gridTemplateColumns, height: HEADER_HEIGHT, minWidth: minTableWidth }}
         >
            {columns.map((column) => {
               const sort = sortColumns.find((item) => item.columnKey === column.key);
               const sortable = column.sortable !== false;
               return (
                  <button
                     key={column.key}
                     type="button"
                     className={`ann-admin-table__headerCell${column.frozen ? " ann-admin-table__cell--frozen" : ""}${sortable ? " ann-admin-table__headerCell--sortable" : ""}`}
                     onClick={() => handleSort(column.key, column.sortable)}
                     disabled={!sortable}
                  >
                     <span className="ann-admin-table__headerLabel">{column.name}</span>
                     {sortable && sort ? (
                        <span className="ann-admin-table__sort">{sort.direction === "ASC" ? "▲" : "▼"}</span>
                     ) : null}
                  </button>
               );
            })}
         </div>
         <div
            ref={bodyRef}
            className="ann-admin-table__body"
            onScroll={handleScroll}
         >
            <div className="ann-admin-table__spacer" style={{ height: totalHeight, minWidth: minTableWidth }}>
               {visibleRows.map((row, index) => {
                  const rowIndex = startIndex + index;
                  const extraClass = rowClass ? rowClass(row) : "";
                  return (
                     <div
                        key={rowKeyGetter(row)}
                        className={`ann-admin-table__row${extraClass ? ` ${extraClass}` : ""}`}
                        style={{
                           top: rowIndex * rowHeight,
                           height: rowHeight,
                           gridTemplateColumns,
                           minWidth: minTableWidth,
                        }}
                     >
                        {columns.map((column) => (
                           <div
                              key={column.key}
                              className={`ann-admin-table__cell${column.frozen ? " ann-admin-table__cell--frozen" : ""}`}
                           >
                              {column.renderCell({ row })}
                           </div>
                        ))}
                     </div>
                  );
               })}
            </div>
         </div>
      </div>
   );
}
